'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'

// walrus
import Box, { BoxProps } from '../../kit/box'
import Pressable from '../../kit/pressable'
import Text from '../../kit/text'
import { accessibilityRole } from '../../kit/_support'

// lib
import { ThemeColor, useHover } from '../_support'
import EmptyState from '../empty-state'
import Skeleton from '../skeleton'

/* -----------------------------------------------------------------------------
 * types
 * -------------------------------------------------------------------------- */

export type SortDirection = 1 | -1

export type DataTableColumn<Row> = {
  align?: 'center' | 'flex-end' | 'flex-start'
  flex?: number
  key: string
  label: string
  minWidth?: number
  render?: (row: Row, rowIndex: number) => React.ReactNode
  sortable?: boolean
  width?: number
}

export type DataTableProps<Row> = Omit<BoxProps, 'children'> & {
  columns: DataTableColumn<Row>[]
  data: Row[]
  emptyDescription?: string
  emptyTitle?: string
  footer?: React.ReactNode
  getRowKey: (row: Row, rowIndex: number) => string
  isLoading?: boolean
  onEmptyAction?: () => void
  emptyActionLabel?: string
  onRowPress?: (row: Row, rowIndex: number) => void
  onSort?: (key: string) => void
  rowBgColor?: (row: Row) => ThemeColor
  skeletonRows?: number
  sortDirection?: SortDirection
  sortKey?: string
}

const DEFAULT_MIN_WIDTH = 120

/* -----------------------------------------------------------------------------
 * cell sizing
 * -------------------------------------------------------------------------- */

const cellSizing = <Row,>(column: DataTableColumn<Row>): BoxProps =>
  typeof column.width === 'number'
    ? { flex: '0 0 auto', width: column.width }
    : {
        flex: `${column.flex ?? 1} 1 0%`,
        minWidth: `${column.minWidth ?? DEFAULT_MIN_WIDTH}px`
      }

/* -----------------------------------------------------------------------------
 * HeaderCell
 * -------------------------------------------------------------------------- */

type HeaderCellProps<Row> = {
  column: DataTableColumn<Row>
  onSort?: (key: string) => void
  sortDirection?: SortDirection
  sortKey?: string
}

function HeaderCell<Row>({
  column,
  onSort,
  sortDirection,
  sortKey
}: HeaderCellProps<Row>) {
  const isSorted = sortKey === column.key
  const caret = isSorted ? (sortDirection === -1 ? ' ▼' : ' ▲') : ''
  const label = (
    <Text font='pro.label.11' textColor='proInk'>
      {`${column.label}${caret}`}
    </Text>
  )

  // the caret is a glyph inside the label, so without an explicit name a
  // screen reader announces "Member ▲"
  const sortedName = isSorted
    ? `${column.label}, sorted ${
        sortDirection === -1 ? 'descending' : 'ascending'
      }`
    : `${column.label}, not sorted`

  return (
    <Box
      accessibilityRole={accessibilityRole('columnheader')}
      alignItems={column.align ?? 'flex-start'}
      justifyContent='center'
      px='16'
      py='12'
      {...cellSizing(column)}
    >
      {column.sortable && onSort ? (
        <Pressable
          accessibilityLabel={sortedName}
          accessibilityRole={accessibilityRole('button')}
          onPress={() => onSort(column.key)}
        >
          {label}
        </Pressable>
      ) : (
        label
      )}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * BodyRow
 * -------------------------------------------------------------------------- */

type BodyRowProps<Row> = {
  bgColor: ThemeColor
  columns: DataTableColumn<Row>[]
  isLast: boolean
  onPress?: () => void
  row: Row
  rowIndex: number
}

function BodyRow<Row>({
  bgColor,
  columns,
  isLast,
  onPress,
  row,
  rowIndex
}: BodyRowProps<Row>) {
  const { hoverProps, isHovered } = useHover()

  const rowStyles: BoxProps = {
    accessibilityRole: accessibilityRole('row'),
    alignItems: 'center',
    bg: isHovered ? 'proSurfaceHover' : bgColor,
    borderBottomWidth: isLast ? 0 : 1,
    borderColor: 'proDividerSoft',
    borderStyle: 'solid',
    flexDirection: 'row'
  }

  const cells = columns.map(column => (
    <Box
      accessibilityRole={accessibilityRole('cell')}
      alignItems={column.align ?? 'flex-start'}
      justifyContent='center'
      key={column.key}
      px='16'
      py='12'
      {...cellSizing(column)}
    >
      {column.render ? (
        column.render(row, rowIndex)
      ) : (
        <Text font='pro.text.13.regular' textColor='proTextBody'>
          {String((row as any)[column.key] ?? '')}
        </Text>
      )}
    </Box>
  ))

  return onPress ? (
    <Pressable {...rowStyles} onPress={onPress} {...hoverProps}>
      {cells}
    </Pressable>
  ) : (
    <Box {...rowStyles} {...hoverProps}>
      {cells}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * DataTable
 * -------------------------------------------------------------------------- */

function DataTable<Row>({
  columns,
  data,
  emptyActionLabel,
  emptyDescription,
  emptyTitle = 'Nothing to show',
  footer,
  getRowKey,
  isLoading = false,
  onEmptyAction,
  onRowPress,
  onSort,
  rowBgColor,
  skeletonRows = 6,
  sortDirection,
  sortKey,
  ...otherProps
}: DataTableProps<Row>) {
  // the row is a flex line, so it needs a floor wide enough for every column
  const minTableWidth = columns.reduce(
    (total, column) =>
      total + (column.width ?? column.minWidth ?? DEFAULT_MIN_WIDTH),
    0
  )

  const isEmpty = !isLoading && data.length === 0

  return (
    <Box
      bg='proSurface'
      borderColor='proBorder'
      borderRadius='proXl'
      borderStyle='solid'
      borderWidth={1}
      boxShadow='proTable'
      overflowX='auto'
      overflowY='hidden'
      {...otherProps}
    >
      <Box minWidth={`${minTableWidth}px`}>
        {/* rows are the only children a role='table' may have, so the footer
         * sits outside it while staying on the min-width track */}
        <Box accessibilityRole={accessibilityRole('table')}>
          <Box
            accessibilityRole={accessibilityRole('row')}
            bg='proSurfaceAlt'
            borderBottomWidth={1}
            borderColor='proDivider'
            borderStyle='solid'
            flexDirection='row'
          >
            {columns.map(column => (
              <HeaderCell
                column={column}
                key={column.key}
                onSort={onSort}
                sortDirection={sortDirection}
                sortKey={sortKey}
              />
            ))}
          </Box>

          {isLoading
            ? Array.from({ length: skeletonRows }).map((_, rowIndex) => (
                <Box
                  accessibilityRole={accessibilityRole('row')}
                  alignItems='center'
                  borderBottomWidth={rowIndex === skeletonRows - 1 ? 0 : 1}
                  borderColor='proDividerSoft'
                  borderStyle='solid'
                  flexDirection='row'
                  key={`skeleton-${rowIndex}`}
                >
                  {columns.map(column => (
                    <Box
                      accessibilityRole={accessibilityRole('cell')}
                      key={column.key}
                      px='16'
                      py='16'
                      {...cellSizing(column)}
                    >
                      <Skeleton width='70%' />
                    </Box>
                  ))}
                </Box>
              ))
            : isEmpty
            ? null
            : data.map((row, rowIndex) => (
                <BodyRow
                  bgColor={rowBgColor ? rowBgColor(row) : 'proSurface'}
                  columns={columns}
                  isLast={rowIndex === data.length - 1 && !footer}
                  key={getRowKey(row, rowIndex)}
                  onPress={
                    onRowPress ? () => onRowPress(row, rowIndex) : undefined
                  }
                  row={row}
                  rowIndex={rowIndex}
                />
              ))}
        </Box>

        {footer ? <Box>{footer}</Box> : null}
      </Box>

      {/* outside the min-width track so an empty table never scrolls sideways */}
      {isEmpty ? (
        <EmptyState
          actionLabel={emptyActionLabel}
          description={emptyDescription}
          onAction={onEmptyAction}
          title={emptyTitle}
        />
      ) : null}
    </Box>
  )
}

export default DataTable
